import {
  Info,
  Settings,
  AlertCircle,
  Calendar,
} from 'lucide-react'
import { useRuleContext } from './RuleDetailLayout'
import { formatDate } from '../../../utils/dateUtils'

export function RuleOverviewTab() {
  const { rule } = useRuleContext()

  if (!rule) return null

  const formatRuleValues = (values: Record<string, any>) => {
    const items: { label: string; value: string }[] = []

    if (values.minDays !== undefined) items.push({ label: 'Días mínimos', value: `${values.minDays} días` })
    if (values.maxDays !== undefined) items.push({ label: 'Días máximos', value: `${values.maxDays} días` })
    if (values.exactDays !== undefined) items.push({ label: 'Días exactos', value: `${values.exactDays} días` })
    if (values.maxPercent !== undefined) items.push({ label: 'Porcentaje máximo', value: `${values.maxPercent}%` })
    if (values.minWorkers !== undefined) items.push({ label: 'Mínimo trabajadores', value: `${values.minWorkers}` })
    if (values.maxPerPosition !== undefined) items.push({ label: 'Máximo por puesto', value: `${values.maxPerPosition}` })
    if (values.maxConcurrent !== undefined) items.push({ label: 'Máximo concurrente', value: `${values.maxConcurrent}` })
    if (values.minDaysAt5Shifts !== undefined) items.push({ label: 'Mín. días a 5R', value: `${values.minDaysAt5Shifts}` })
    if (values.minDaysLast10Years !== undefined) items.push({ label: 'Mín. días últimos 10 años', value: `${values.minDaysLast10Years}` })
    if (values.month !== undefined && values.day !== undefined) items.push({ label: 'Fecha límite', value: `${values.day}/${values.month}` })
    if (values.maxPerTurnPosition !== undefined) items.push({ label: 'Máx. por turno/puesto', value: `${values.maxPerTurnPosition}` })
    if (values.maxPerDay !== undefined) items.push({ label: 'Máximo por día', value: `${values.maxPerDay}` })
    if (values.blockedPeriodTypes?.length) items.push({ label: 'Períodos bloqueados', value: values.blockedPeriodTypes.join(', ') })
    if (values.requiresAt5Shifts) items.push({ label: 'Requiere 5R', value: 'Sí' })
    if (values.notEnjoyed2Years) items.push({ label: 'No disfrutado 2 años', value: 'Sí' })

    return items
  }

  const formatConditions = (conditions: Record<string, any> | null) => {
    if (!conditions || Object.keys(conditions).length === 0) return []

    const items: { label: string; value: string }[] = []

    if (conditions.hasStop !== undefined) items.push({ label: 'Tiene parada', value: conditions.hasStop ? 'Sí' : 'No' })
    if (conditions.isExtension !== undefined) items.push({ label: 'Es prórroga', value: conditions.isExtension ? 'Sí' : 'No' })
    if (conditions.daysRequested !== undefined) items.push({ label: 'Días solicitados', value: `${conditions.daysRequested}` })
    if (conditions.isPresecadoCelulosa !== undefined) items.push({ label: 'Presecado celulosa', value: conditions.isPresecadoCelulosa ? 'Sí' : 'No' })

    return items
  }

  const valueItems = formatRuleValues(rule.values)
  const conditionItems = formatConditions(rule.conditions)

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Description */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <Info className="h-5 w-5" />
            Descripción
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            {rule.description || <span className="text-gray-400 italic">Sin descripción</span>}
          </p>
        </div>

        {/* Error Message */}
        {rule.errorMessage && (
          <div className="card border-l-4 border-error-500">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-error-500" />
              Mensaje de Error
            </h2>
            <p className="text-gray-700 dark:text-gray-300">{rule.errorMessage}</p>
          </div>
        )}

        {/* Values */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Valores de la Regla
          </h2>
          {valueItems.length > 0 ? (
            <div className="space-y-3">
              {valueItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
                  <span className="text-gray-600 dark:text-gray-400">{item.label}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{item.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 italic">Sin valores configurados</p>
          )}
        </div>

        {/* Conditions */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
            Condiciones de Aplicación
          </h2>
          {conditionItems.length > 0 ? (
            <div className="space-y-3">
              {conditionItems.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700 last:border-0">
                  <span className="text-gray-600 dark:text-gray-400">{item.label}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{item.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 italic">Aplica a todas las solicitudes del tipo</p>
          )}
        </div>
      </div>

      {/* Metadata */}
      <div className="card">
        <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
          Información Adicional
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg text-center">
            <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Prioridad</span>
            <p className="font-semibold text-xl text-gray-900 dark:text-white">{rule.priority}</p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg text-center">
            <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Orden</span>
            <p className="font-semibold text-xl text-gray-900 dark:text-white">{rule.sortOrder}</p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Calendar className="h-3.5 w-3.5" />
              <span>Creada</span>
            </div>
            <p className="font-medium text-gray-900 dark:text-white">
              {formatDate(rule.createdAt)}
            </p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-slate-800 rounded-lg text-center">
            <div className="flex items-center justify-center gap-1 text-sm text-gray-500 dark:text-gray-400 mb-1">
              <Calendar className="h-3.5 w-3.5" />
              <span>Actualizada</span>
            </div>
            <p className="font-medium text-gray-900 dark:text-white">
              {formatDate(rule.updatedAt)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
